// src/hooks/useModalData.js
import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { Logger } from '../utils/logger';

const logger = new Logger('useModalData');

// Simple in-memory cache with TTL
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Custom hook for fetching modal data with caching, loading states, and error handling
 * Provides consistent data fetching pattern across all modal components
 *
 * @param {string} url - API endpoint URL
 * @param {boolean} open - Whether modal is open (triggers fetch)
 * @param {Object} options - Fetch options
 * @param {boolean} options.cache - Enable caching (default: true)
 * @param {number} options.ttl - Cache TTL in ms (default: 5 minutes)
 * @param {Function} options.transform - Transform function for response data
 * @param {Function} options.onError - Error callback
 * @returns {Object} { data, loading, error, refetch }
 *
 * @example
 * const { data, loading, error } = useModalData(
 *   `${API_BASE_URL}/employees/${employeeId}/performance`,
 *   open,
 *   { cache: true }
 * );
 */
export const useModalData = (url, open, options = {}) => {
  const {
    cache: enableCache = true,
    ttl = CACHE_TTL,
    transform,
    onError
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  const fetchData = async () => {
    if (!url || !open) return;

    // Check cache first
    if (enableCache && cache.has(url)) {
      const cached = cache.get(url);
      if (Date.now() - cached.timestamp < ttl) {
        logger.debug(`Cache hit for: ${url}`);
        setData(cached.data);
        return;
      } else {
        // Expired cache entry
        cache.delete(url);
      }
    }

    // Abort previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      logger.debug(`Fetching: ${url}`);

      // Convert full URL to relative path for apiFetch
      // Remove API_BASE_URL if present (for backwards compatibility)
      const endpoint = url.startsWith(API_BASE_URL)
        ? url.substring(API_BASE_URL.length + 1) // +1 to remove leading slash
        : url;

      const response = await apiFetch(endpoint, {
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      let responseData = await response.json();

      // Apply transform if provided
      if (transform && typeof transform === 'function') {
        responseData = transform(responseData);
      }

      setData(responseData);

      // Cache the result
      if (enableCache) {
        cache.set(url, {
          data: responseData,
          timestamp: Date.now()
        });
      }

      logger.debug(`Success: ${url}`);
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        logger.debug(`Request aborted: ${url}`);
        return;
      }

      logger.error(`Error fetching ${url}:`, err);
      const errorMessage = `Failed to load data: ${err.message}`;
      setError(errorMessage);

      if (onError && typeof onError === 'function') {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchData();
    } else {
      // Reset data when modal closes
      setData(null);
      setError(null);
    }

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [url, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Provide refetch function for manual refresh
  const refetch = () => {
    // Clear cache for this URL
    if (enableCache && cache.has(url)) {
      cache.delete(url);
    }
    fetchData();
  };

  return { data, loading, error, refetch };
};

/**
 * Clear entire cache or specific URL
 * @param {string} url - Optional URL to clear, if not provided clears entire cache
 */
export const clearCache = (url) => {
  if (url) {
    cache.delete(url);
    logger.debug(`Cache cleared for: ${url}`);
  } else {
    cache.clear();
    logger.debug('Entire cache cleared');
  }
};

/**
 * Get cache statistics
 * @returns {Object} Cache stats
 */
export const getCacheStats = () => {
  return {
    size: cache.size,
    entries: Array.from(cache.keys())
  };
};
